/**
 * overlayQueue — the pure model behind the CRM's single overlay stack
 * (streamline plan D-2). React-free by design: every rule that decides what a
 * recruiter sees in the bottom-right corner is a function you can call from a
 * test, because the bugs this replaces (a hot lead sliced off the stack, an SMS
 * toast dropped to make room for a newer one, a 15s timer that burned down
 * while the card was hidden) were all invisible in the old per-host code.
 *
 * The model in one paragraph: every overlay card is an item with a PRIORITY
 * (lower wins) and an optional auto-dismiss BUDGET. The top `MAX_VISIBLE` items
 * render; the rest queue. Nothing is ever dropped to make room — overflow waits
 * its turn behind a "+N more" pill. A budget is consumed ONLY while the card is
 * actually on screen, so a card that spends twenty seconds behind the overflow
 * still gets its full fifteen seconds of attention when it surfaces, and a card
 * displaced by a higher-priority arrival keeps the time it already served.
 *
 * Ordering is (priority asc, createdAt desc). `Array.prototype.sort` is stable
 * per ES2019, so items that tie on both keys — a batch that arrives from one
 * feed response with identical timestamps — keep the order the feed gave them.
 *
 * The four priority classes come from what interrupting a recruiter is worth:
 * a lead waiting on a call (10) beats a booking they already have (20), which
 * beats an agent conversation the agent is still holding (30), which beats an
 * inbound text (40), which beats a rating prompt for a call that already
 * happened (50).
 */

/** The eight alert kinds the alerts feed can produce (mirrors `alertKinds`). */
export type OverlayAlertKind =
  | 'lead_call'
  | 'referral_consented'
  | 'agent_call_request'
  | 'agent_handoff_review'
  | 'agent_hostile_or_distress'
  | 'agent_human_requested'
  | 'agent_unknown_answer'
  | 'agent_bot_question';

/** Every card kind the stack can hold. */
export type OverlayKind = OverlayAlertKind | 'callback' | 'sms' | 'rating';

/**
 * Which producer owns an item. Feeds are replaced wholesale by their owner
 * (see `replaceSource`), so an item's source — not its kind — decides who may
 * reap it. `test` is the QA test-fire pseudo-feed: it spans kinds and must
 * survive every real feed refresh.
 */
export type OverlaySource = 'alerts' | 'callback' | 'sms' | 'rating' | 'test';

/** How many cards render at once; the remainder queues behind the pill. */
export const MAX_VISIBLE = 3;

export const PRIORITY_ALERT = 10;
export const PRIORITY_CALLBACK = 20;
export const PRIORITY_AGENT_ALERT = 30;
export const PRIORITY_SMS = 40;
export const PRIORITY_RATING = 50;

/** Auto-dismiss budgets, in ms of ON-SCREEN time. `null` = never self-dismiss. */
export const DISMISS_AFTER_CALLBACK_MS = 60_000;
export const DISMISS_AFTER_SMS_MS = 15_000;
export const DISMISS_AFTER_RATING_MS = 30_000;

export interface OverlayItem<P = unknown> {
  /** Stable across refreshes — this is what preserves a card's served time. */
  id: string;
  kind: OverlayKind;
  /** Lower renders first. See `priorityFor`. */
  priority: number;
  /** Epoch ms. Ties break newest-first within a priority class. */
  createdAt: number;
  /** On-screen ms after which the card self-dismisses; `null` = never. */
  dismissAfterMs: number | null;
  /** On-screen ms served so far. Frozen while the card is queued. */
  msVisible: number;
  /** Whoever owns the item and may replace/reap it. */
  source: OverlaySource;
  /** Whatever the card needs to render. Opaque to this module. */
  payload: P;
}

/** An agent escalation card (they rank below a booked callback). */
export function isAgentAlertKind(kind: OverlayKind): boolean {
  return kind.startsWith('agent_');
}

/** Any of the eight alert-feed kinds. */
export function isAlertKind(kind: OverlayKind): kind is OverlayAlertKind {
  return kind !== 'callback' && kind !== 'sms' && kind !== 'rating';
}

/** The feed a kind belongs to by default (the test feed overrides it). */
export function sourceFor(kind: OverlayKind): OverlaySource {
  if (kind === 'callback' || kind === 'sms' || kind === 'rating') return kind;
  return 'alerts';
}

export function priorityFor(kind: OverlayKind): number {
  if (kind === 'callback') return PRIORITY_CALLBACK;
  if (kind === 'sms') return PRIORITY_SMS;
  if (kind === 'rating') return PRIORITY_RATING;
  return isAgentAlertKind(kind) ? PRIORITY_AGENT_ALERT : PRIORITY_ALERT;
}

/**
 * The auto-dismiss budget for a kind. Alerts NEVER self-dismiss: a hot lead
 * that times itself off the screen unseen is the exact failure the alert exists
 * to prevent (its own escalation clock is a separate, server-side thing).
 */
export function dismissAfterFor(kind: OverlayKind): number | null {
  if (kind === 'callback') return DISMISS_AFTER_CALLBACK_MS;
  if (kind === 'sms') return DISMISS_AFTER_SMS_MS;
  if (kind === 'rating') return DISMISS_AFTER_RATING_MS;
  return null;
}

export interface CreateItemInput<P> {
  id: string;
  kind: OverlayKind;
  payload: P;
  /** Epoch ms; defaults to now. */
  createdAt?: number;
  /** Overrides the kind default (the test feed keeps the kind's own budget). */
  source?: OverlaySource;
  dismissAfterMs?: number | null;
  priority?: number;
}

export function createItem<P>(input: CreateItemInput<P>): OverlayItem<P> {
  return {
    id: input.id,
    kind: input.kind,
    priority: input.priority ?? priorityFor(input.kind),
    createdAt: input.createdAt ?? Date.now(),
    dismissAfterMs: input.dismissAfterMs === undefined ? dismissAfterFor(input.kind) : input.dismissAfterMs,
    msVisible: 0,
    source: input.source ?? sourceFor(input.kind),
    payload: input.payload,
  };
}

/** (priority asc, createdAt desc), stable on ties — see the file header. */
export function orderItems<P>(items: OverlayItem<P>[]): OverlayItem<P>[] {
  return items.slice().sort((a, b) => a.priority - b.priority || b.createdAt - a.createdAt);
}

/**
 * Split the stack into what renders and what waits. The queued half is a
 * waiting room, never a bin: callers must render a "+N more" affordance for it.
 */
export function selectVisible<P>(
  items: OverlayItem<P>[],
  max: number = MAX_VISIBLE,
): { visible: OverlayItem<P>[]; queued: OverlayItem<P>[] } {
  const ordered = orderItems(items);
  return { visible: ordered.slice(0, max), queued: ordered.slice(max) };
}

/** Add an item, or update one in place (keeping the time it has served). */
export function upsert<P>(items: OverlayItem<P>[], item: OverlayItem<P>): OverlayItem<P>[] {
  const idx = items.findIndex((i) => i.id === item.id);
  if (idx === -1) return [...items, item];
  const next = items.slice();
  next[idx] = { ...item, msVisible: items[idx].msVisible };
  return next;
}

export function remove<P>(items: OverlayItem<P>[], id: string): OverlayItem<P>[] {
  return items.filter((i) => i.id !== id);
}

/** Give a card its full budget back (the rating prompt does this on a star tap). */
export function resetBudget<P>(items: OverlayItem<P>[], id: string): OverlayItem<P>[] {
  return items.map((i) => (i.id === id ? { ...i, msVisible: 0 } : i));
}

/**
 * Swap one feed's whole slice for a fresh one, leaving every other source
 * untouched. Cards that survive the refresh keep their served time; cards the
 * feed no longer lists are gone (that is how a server-expired alert leaves the
 * screen). The test feed is a separate source, so a QA card is never reaped by
 * a real refresh.
 */
export function replaceSource<P>(
  items: OverlayItem<P>[],
  source: OverlaySource,
  incoming: OverlayItem<P>[],
): OverlayItem<P>[] {
  const previous = new Map(items.filter((i) => i.source === source).map((i) => [i.id, i]));
  const others = items.filter((i) => i.source !== source);
  const rebuilt = incoming.map((inc) => {
    const prev = previous.get(inc.id);
    return prev ? { ...inc, msVisible: prev.msVisible } : inc;
  });
  return [...others, ...rebuilt];
}

export interface TickResult<P> {
  items: OverlayItem<P>[];
  /** Items whose budget ran out on this tick — the caller tells their feed. */
  expired: OverlayItem<P>[];
  changed: boolean;
}

/**
 * Advance the clock by `elapsedMs`. Only the cards currently on screen spend
 * their budget; queued cards are frozen. Items whose budget is exhausted come
 * back in `expired` AND are removed from `items` — the caller is responsible
 * for telling the owning feed, or the next refresh will simply re-add them.
 */
export function tick<P>(
  items: OverlayItem<P>[],
  elapsedMs: number,
  max: number = MAX_VISIBLE,
): TickResult<P> {
  if (elapsedMs <= 0 || items.length === 0) return { items, expired: [], changed: false };
  const { visible } = selectVisible(items, max);
  const onScreen = new Set(visible.map((i) => i.id));
  const next: OverlayItem<P>[] = [];
  const expired: OverlayItem<P>[] = [];
  for (const item of items) {
    if (!onScreen.has(item.id)) {
      next.push(item);
      continue;
    }
    const advanced = { ...item, msVisible: item.msVisible + elapsedMs };
    if (advanced.dismissAfterMs !== null && advanced.msVisible >= advanced.dismissAfterMs) {
      expired.push(advanced);
      continue;
    }
    next.push(advanced);
  }
  return { items: next, expired, changed: true };
}
